import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Flame, MapPin, Star } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";
import HGCategoryArt from "../components/HGCategoryArt";

const HERO_CATEGORIES = [
  { key: "street_food", label: "Street Food", gradient: "from-pink-500/30 to-rose-500/10" },
  { key: "bbq",         label: "BBQ",         gradient: "from-amber-500/30 to-red-500/10" },
  { key: "seafood",     label: "Seafood",     gradient: "from-cyan-500/30 to-blue-500/10" },
  { key: "mexican",     label: "Mexican",     gradient: "from-red-500/30 to-orange-500/10" },
  { key: "soul_food",   label: "Soul Food",   gradient: "from-amber-600/30 to-yellow-500/10" },
  { key: "desserts",    label: "Desserts",    gradient: "from-pink-400/30 to-fuchsia-500/10" },
  { key: "drinks",      label: "Drinks",      gradient: "from-cyan-400/30 to-teal-500/10" },
  { key: "snacks",      label: "Snacks",      gradient: "from-orange-400/30 to-amber-500/10" },
  { key: "plates",      label: "Plates",      gradient: "from-emerald-500/30 to-green-500/10" },
  { key: "meal_prep",   label: "Meal Prep",   gradient: "from-lime-500/30 to-emerald-500/10" },
  { key: "pop_ups",     label: "Pop Ups",     gradient: "from-violet-500/30 to-purple-500/10" },
  { key: "cultural",    label: "Cultural",    gradient: "from-indigo-500/30 to-blue-500/10" },
];

const TESTIMONIALS = [
  { name: "Jasmin K.", quote: "Found my new favorite birria spot in Brooklyn within 30 seconds of opening the app.", role: "Buyer" },
  { name: "Marco V.", quote: "Hidden Gem doubled my Saturday pickup orders. Zero fees. Direct cash flow.", role: "Vendor · BBQ" },
  { name: "Tia R.", quote: "Every scroll surfaces a new neighborhood gem I can actually eat tonight. Dangerously good.", role: "Buyer" },
];

function VendorCard({ v, onClick }) {
  const vp = v.vendor_profile || {};
  return (
    <button
      onClick={onClick}
      className="shrink-0 w-44 rounded-3xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-pink-400/50 transition-colors text-left"
      data-testid={`trending-vendor-${v.user_id}`}
    >
      <div className="aspect-[3/4] bg-zinc-800 relative">
        {v.sample_image && (
          <img src={fileUrl(v.sample_image)} alt={vp.business_name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-2 left-2 right-2">
          <div className="flex items-center gap-1">
            <p className="text-sm font-black text-white truncate">{vp.business_name || v.name}</p>
            <VerifiedBadge verified={v.is_verified} />
          </div>
          {vp.location && (
            <p className="text-[10px] text-zinc-300 inline-flex items-center gap-1 mt-0.5">
              <MapPin className="w-2.5 h-2.5" /> {vp.location}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function FeaturedCard({ p, onClick }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 w-56 sm:w-64 rounded-3xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-pink-400/50 transition-colors text-left"
      data-testid={`featured-product-${p.product_id}`}
    >
      <div className="aspect-[4/3] bg-zinc-800 relative">
        {p.image_paths?.[0] && (
          <img src={fileUrl(p.image_paths[0])} alt={p.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
        <div className="absolute bottom-2 left-2 right-2">
          <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold inline-flex items-center gap-1">
            @{p.vendor_name} <VerifiedBadge verified={p.vendor_verified} />
          </p>
          <p className="text-sm font-black text-white truncate">{p.name}</p>
          <p className="text-pink-300 font-black text-base">${p.price?.toFixed(2)}</p>
        </div>
      </div>
    </button>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/discover/trending-vendors", { params: { limit: 8 } })
      .then(({ data }) => setTrending(data))
      .catch(() => setTrending([]));
    api.get("/discover/featured-products", { params: { limit: 8 } })
      .then(({ data }) => setFeatured(data))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-black text-white">
      {/* Background visuals */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-pink-600/30 blur-[140px]" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/20 blur-[140px]" />
      </div>

      {/* Top bar */}
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between sticky top-0 z-30 glass-strong">
        <span className="text-2xl font-black tracking-tighter text-gradient-neon">Hidden Gem</span>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-4 py-2" data-testid="landing-login-link">Log in</Link>
          <button onClick={() => navigate("/register")} className="text-sm bg-white text-black font-semibold px-4 py-2 rounded-full hover:bg-zinc-200 transition-colors" data-testid="landing-signup-btn">Sign up</button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 sm:px-10 pt-12 pb-16 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-pink-300 mb-6">
            <Sparkles className="w-3 h-3" /> Small. Local. Loud.
          </div>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95]">
            Discover the<br />
            <span className="text-gradient-neon">hidden gems</span><br />
            of your block.
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-zinc-400 max-w-2xl leading-relaxed">
            Real food. Real people. Real flavor. Hidden Gem is where neighborhood cooks, late-night bakers, and street pitmasters share what they make — and you order it straight from them. No middlemen. No franchise. Just the best plate on your block.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate("/register")} className="group inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold text-base glow-pink transition-all" data-testid="hero-get-started-btn">
              Start exploring <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate("/login")} className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold" data-testid="hero-login-btn">
              I already have an account
            </button>
          </div>
        </motion.div>
      </section>

      {/* Trending vendors */}
      {trending.length > 0 && (
        <section className="px-6 sm:px-10 py-8 max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-2 mb-1">
              <Flame className="w-6 h-6 text-pink-400" /> Trending this week
            </h2>
            <p className="text-zinc-400 text-sm mb-5">Vendors blowing up on Hidden Gem right now.</p>
          </motion.div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="trending-vendors-row">
            {trending.map((v) => (
              <VendorCard key={v.user_id} v={v} onClick={() => navigate("/login")} />
            ))}
          </div>
        </section>
      )}

      {/* Categories grid */}
      <section className="px-6 sm:px-10 py-8 max-w-6xl mx-auto">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-black tracking-tighter mb-5">
          Browse by category
        </motion.h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="categories-grid">
          {HERO_CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => navigate(`/login`)}
              className={`relative rounded-3xl p-5 bg-gradient-to-br ${c.gradient} border border-white/10 hover:border-pink-400/50 hover:scale-[1.02] transition-all text-left group overflow-hidden`}
              data-testid={`landing-category-${c.key}`}
            >
              <div className="mb-2 transition-transform group-hover:scale-105">
                <HGCategoryArt slug={c.key} size={80} />
              </div>
              <span className="text-lg font-black tracking-tight">{c.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Featured gems */}
      {featured.length > 0 && (
        <section className="px-6 sm:px-10 py-8 max-w-6xl mx-auto">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-black tracking-tighter mb-5 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-cyan-400" /> Featured gems
          </motion.h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="featured-products-row">
            {featured.map((p) => (
              <FeaturedCard key={p.product_id} p={p} onClick={() => navigate("/login")} />
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="px-6 sm:px-10 py-12 max-w-6xl mx-auto">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-black tracking-tighter mb-7">
          What people are saying
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-3xl p-5 bg-white/[0.03] border border-white/10"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-0.5 mb-2 text-amber-400">
                {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400" />)}
              </div>
              <p className="text-zinc-200 leading-relaxed">"{t.quote}"</p>
              <p className="mt-3 text-xs text-pink-300 font-bold">{t.name} <span className="text-zinc-500 font-normal">— {t.role}</span></p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 sm:px-10 py-16 max-w-4xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-4xl sm:text-6xl font-black tracking-tighter">
            Your block is full of <span className="text-gradient-neon">hidden gems</span>.
          </h2>
          <p className="mt-4 text-zinc-400 text-lg">Open one tab. Tap the feed. You're in.</p>
          <button onClick={() => navigate("/register")} className="mt-8 inline-flex items-center gap-2 h-14 px-10 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold text-base glow-pink" data-testid="cta-bottom-btn">
            Get started — free <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </section>

      <footer className="px-6 sm:px-10 py-8 border-t border-white/5 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} Hidden Gem · Made for the underdogs.
      </footer>
    </div>
  );
}
